import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sendEmail } from '../../services/EmailService';
import imageCompression from "browser-image-compression";
import "./Signup.css";

const SOCIAL_CREDENTIAL_PLATFORMS = {
    "Starter Socials (No video)": ["facebook", "twitter", "instagram", "linkedin"],
    "Advanced Socials (Supports Video)": ["facebook", "twitter", "instagram", "linkedin", "youtube", "tiktok"],
    "Professional Socials (Supports Video)": ["facebook", "twitter", "instagram", "linkedin", "youtube", "tiktok"],
    "Socials Bundle": ["facebook", "twitter", "instagram", "linkedin", "youtube", "tiktok"],
    "All-in-One Growth Bundle": ["facebook", "twitter", "instagram", "linkedin", "youtube", "tiktok"]
};

const SOCIAL_LINK_PLATFORMS = {
    "Starter Socials (No video)": ["twitter", "facebook", "instagram", "linkedin"],
    "Advanced Socials (Supports Video)": ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"],
    "Professional Socials (Supports Video)": ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"],
    "Socials Bundle": ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"],
    "All-in-One Growth Bundle": ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"]
};

function getPlanType(plan) {
    if (
        [
            "All-in-One Growth Bundle",
            "Hosting Bundle",
            "Starter Website",
            "Advanced Website",
            "Professional Website"
        ].includes(plan)
    ) return "website";
    if (
        [
            "All-in-One Growth Bundle",
            "Socials Bundle",
            "Starter Socials (No video)",
            "Advanced Socials (Supports Video)",
            "Professional Socials (Supports Video)"
        ].includes(plan)
    ) return "social";
    if (
        [
            "All-in-One Growth Bundle",
            "Hosting Bundle",
            "Single Landing Page",
            "Team Landing Page"
        ].includes(plan)
    ) return "landing";
    if (
        [
            "Growth Newsletter",
            "Professional Newsletter"
        ].includes(plan)
    ) return "newsletter";
    return "";
}

const initialTestimonial = { name: "", picture: null, message: "" };
const initialOpenHouse = { start: "", end: "" };

// Add this helper to ensure all socialCredentials keys exist
function ensureSocialCredentials(form, credPlatforms) {
    const updated = { ...form.socialCredentials };
    credPlatforms.forEach(platform => {
        if (!updated[platform]) {
            updated[platform] = { username: "", password: "" };
        }
    });
    return updated;
}

// Add this near the top, after PLAN_OPTIONS
const WEBSITE_TEMPLATES = [
    {
        id: "base",
        name: "Base",
        description: "A solid foundation with essential features and a clean design.",
        img: require("../../assets/imgs/previews/agent/base.jpg")
    },
    {
        id: "luxury",
        name: "Luxury",
        description: "Elegant and sophisticated with high-quality visuals.",
        img: require("../../assets/imgs/previews/agent/luxury.jpg")
    },
    {
        id: "metropolitan",
        name: "Metropolitan",
        description: "Modern living with neutral tones and refined visuals.",
        img: require("../../assets/imgs/previews/agent/metropolitan.jpg")
    },
    {
        id: "skyline",
        name: "Skyline",
        description: "Condo-focused theme with high-rise and city views.",
        img: require("../../assets/imgs/previews/agent/skyline.jpg")
    },
    {
        id: "vibrance",
        name: "Vibrance",
        description: "Energetic, bold, and full of life.",
        img: require("../../assets/imgs/previews/agent/vibrance.jpg")
    },
    {
        id: "futuristic",
        name: "Futuristic",
        description: "High-tech aesthetics with vibrant neon and abstract visuals.",
        img: require("../../assets/imgs/previews/agent/futuristic.jpg")
    }
];

function buildEmailContent(form, planType) {
    // Inline CSS for table styling
    const tableStyle = "border-collapse:collapse;width:100%;font-family:sans-serif;font-size:15px;";
    const thStyle = "background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;";
    const tdStyle = "padding:8px;border-bottom:1px solid #eee;";

    // Helper to render a row if value exists
    const row = (label, value) =>
        value ? `<tr><th style="${thStyle}">${label}</th><td style="${tdStyle}">${value}</td></tr>` : "";

    // General Info
    let html = `<h2 style="font-family:sans-serif;">Signup Request</h2>
    <table style="${tableStyle}">
        ${row("Name", form.name)}
        ${row("Email", form.email)}
        ${row("Phone", form.phone)}
        ${row("Company", form.company)}
        ${row("Selected Plan", form.plan)}
        ${row("Bio/About Me", form.bio)}
        ${row("Quote", form.quote)}
    </table>`;

    // Testimonials
    if (form.testimonials && form.testimonials.length > 0 && form.testimonials.some(t => t.name || t.message)) {
        html += `<h3 style="font-family:sans-serif;">Testimonials</h3>
        <table style="${tableStyle}">`;
        form.testimonials.forEach((t, idx) => {
            if (t.name || t.message) {
                html += `
                <tr>
                    <th style="${thStyle}">Name</th>
                    <td style="${tdStyle}">${t.name || ""}</td>
                </tr>
                <tr>
                    <th style="${thStyle}">Message</th>
                    <td style="${tdStyle}">${t.message || ""}</td>
                </tr>
                `;
            }
        });
        html += `</table>`;
    }

    // Company Info
    if (form.companyInfo && (
        form.companyInfo.name || form.companyInfo.phone || form.companyInfo.email || form.companyInfo.address
    )) {
        html += `<h3 style="font-family:sans-serif;">Company Information</h3>
        <table style="${tableStyle}">
            ${row("Company Name", form.companyInfo.name)}
            ${row("Company Phone", form.companyInfo.phone)}
            ${row("Company Email", form.companyInfo.email)}
            ${row("Company Address", form.companyInfo.address)}
        </table>`;
    }

    // Website Info
    if (planType === "website" || planType === "landing") {
        html += `<h3 style="font-family:sans-serif;">Website Information</h3>
        <table style="${tableStyle}">
            ${row("Website Quote", form.websiteQuote)}
            ${row("Domain", form.domain)}
        </table>`;
    }

    // Social Media Info
    if (planType === "social") {
        html += `<h3 style="font-family:sans-serif;">Social Media Information</h3>
        <table style="${tableStyle}">
            ${row("Post For You?", form.postForYou ? "Yes" : "No")}
        </table>`;
        if (form.postForYou) {
            html += `<h4 style="font-family:sans-serif;">Account Credentials</h4>
            <table style="${tableStyle}">`;
            Object.keys(form.socialCredentials).forEach(platform => {
                const creds = form.socialCredentials[platform];
                if (creds.username || creds.password) {
                    html += `
                    <tr>
                        <th style="${thStyle}">${platform.charAt(0).toUpperCase() + platform.slice(1)} Username</th>
                        <td style="${tdStyle}">${creds.username}</td>
                    </tr>
                    <tr>
                        <th style="${thStyle}">${platform.charAt(0).toUpperCase() + platform.slice(1)} Password</th>
                        <td style="${tdStyle}">${creds.password ? "********" : ""}</td>
                    </tr>
                    `;
                }
            });
            html += `</table>`;
        }
        html += `<h4 style="font-family:sans-serif;">Social Links</h4>
        <table style="${tableStyle}">
            ${row("Twitter/X", form.socialLinks.twitter)}
            ${row("Facebook", form.socialLinks.facebook)}
            ${row("Instagram", form.socialLinks.instagram)}
            ${row("YouTube", form.socialLinks.youtube)}
            ${row("LinkedIn", form.socialLinks.linkedin)}
            ${row("TikTok", form.socialLinks.tiktok)}
        </table>`;
    }

    // Landing Page Info
    if (planType === "landing") {
        html += `<h3 style="font-family:sans-serif;">Landing Page Information</h3>
        <table style="${tableStyle}">
            ${row("Title", form.title)}
            ${row("Listing Address", form.listing.address)}
            ${row("Postal Code", form.listing.postal)}
            ${row("Size", form.listing.size)}
            ${row("Year Built", form.listing.yearBuilt)}
            ${row("Lot Size", form.listing.lotSize)}
            ${row("Bedrooms", form.listing.bedrooms)}
            ${row("Bathrooms", form.listing.bathrooms)}
            ${row("Price", form.listing.price)}
        </table>`;
        // Open Houses
        if (form.listing.openHouses && form.listing.openHouses.length > 0) {
            html += `<h4 style="font-family:sans-serif;">Open Houses</h4>
            <table style="${tableStyle}">
                <tr>
                    <th style="${thStyle}">Start</th>
                    <th style="${thStyle}">End</th>
                </tr>`;
            form.listing.openHouses.forEach(o => {
                if (o.start || o.end) {
                    html += `<tr>
                        <td style="${tdStyle}">${o.start ? new Date(o.start).toLocaleString() : ""}</td>
                        <td style="${tdStyle}">${o.end ? new Date(o.end).toLocaleString() : ""}</td>
                    </tr>`;
                }
            });
            html += `</table>`;
        }
    }

    // Add after Website Info section
    if ((planType === "website" || planType === "landing" || form.plan === "All-in-One Growth Bundle" || form.plan === "Hosting Bundle") && form.preferredTemplate) {
        const templateObj = WEBSITE_TEMPLATES.find(t => t.id === form.preferredTemplate);
        html += `<h3 style="font-family:sans-serif;">Preferred Website Template</h3>
        <table style="${tableStyle}">
            <tr>
                <th style="${thStyle}">Template</th>
                <td style="${tdStyle}">${templateObj ? templateObj.name : form.preferredTemplate}</td>
            </tr>
            <tr>
                <th style="${thStyle}">Description</th>
                <td style="${tdStyle}">${templateObj ? templateObj.description : ""}</td>
            </tr>
        </table>`;
    }

    return html;
}

const Signup = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialPlan = location.state?.selectedPlan || "";

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState('');

    const [selectedPlan, setSelectedPlan] = useState(initialPlan);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        picture: null,
        plan: initialPlan,
        bio: "",
        quote: "",
        testimonials: [ { ...initialTestimonial } ],
        companyInfo: {
            name: "",
            phone: "",
            email: "",
            logo: null,
            address: ""
        },
        // Website Info
        websiteMainPicture: null,
        websiteQuote: "",
        domain: "",
        // Social Media Info
        postForYou: false,
        socialCredentials: {
            facebook: { username: "", password: "" },
            twitter: { username: "", password: "" },
            instagram: { username: "", password: "" },
            youtube: { username: "", password: "" },
            tiktok: { username: "", password: "" }
        },
        socialLinks: {
            twitter: "",
            facebook: "",
            instagram: "",
            youtube: "",
            linkedin: "",
            tiktok: ""
        },
        // Landing Page Info
        title: "",
        landingMain: null,
        listing: {
            address: "",
            postal: "",
            size: "",
            yearBuilt: "",
            lotSize: "",
            bedrooms: "",
            bathrooms: "",
            price: "",
            pictures: [],
            openHouses: [ { ...initialOpenHouse } ]
        },
        preferredTemplate: "", // <-- add this line
    });
    const [errors, setErrors] = useState({});
    const planType = getPlanType(selectedPlan);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, files, checked } = e.target;
        if (name.startsWith("companyInfo.")) {
            const key = name.split(".")[1];
            setForm(f => ({
                ...f,
                companyInfo: { ...f.companyInfo, [key]: type === "file" ? files[0] : value }
            }));
        } else if (name.startsWith("socialLinks.")) {
            const key = name.split(".")[1];
            setForm(f => ({
                ...f,
                socialLinks: { ...f.socialLinks, [key]: value }
            }));
        } else if (name.startsWith("socialCredentials.")) {
            const [, platform, field] = name.split("."); // Remove unused _
            setForm(f => ({
                ...f,
                socialCredentials: {
                    ...f.socialCredentials,
                    [platform]: { ...f.socialCredentials[platform], [field]: value }
                }
            }));
        } else if (name.startsWith("listing.")) {
            const key = name.split(".")[1];
            setForm(f => ({
                ...f,
                listing: { ...f.listing, [key]: type === "file" ? Array.from(files) : value }
            }));
        } else if (name === "picture" || name === "websiteMainPicture" || name === "landingMain") {
            setForm(f => ({ ...f, [name]: files[0] }));
        } else if (name === "postForYou") {
            setForm(f => ({ ...f, postForYou: checked }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    // Testimonials handlers
    const handleTestimonialChange = (idx, field, value, files) => {
        setForm(f => {
            const testimonials = f.testimonials.map((t, i) =>
                i === idx ? { ...t, [field]: field === "picture" ? files[0] : value } : t
            );
            return { ...f, testimonials };
        });
    };
    const addTestimonial = () => {
        setForm(f => ({ ...f, testimonials: [ ...f.testimonials, { ...initialTestimonial } ] }));
    };
    const removeTestimonial = (idx) => {
        setForm(f => ({ ...f, testimonials: f.testimonials.filter((_, i) => i !== idx) }));
    };

    // Open House handlers
    const handleOpenHouseChange = (idx, field, value) => {
        setForm(f => {
            const openHouses = f.listing.openHouses.map((o, i) =>
                i === idx ? { ...o, [field]: value } : o
            );
            return { ...f, listing: { ...f.listing, openHouses } };
        });
    };
    const addOpenHouse = () => {
        setForm(f => ({
            ...f,
            listing: { ...f.listing, openHouses: [ ...f.listing.openHouses, { ...initialOpenHouse } ] }
        }));
    };
    const removeOpenHouse = (idx) => {
        setForm(f => ({
            ...f,
            listing: { ...f.listing, openHouses: f.listing.openHouses.filter((_, i) => i !== idx) }
        }));
    };

    // Redirect to pricing page
    const handleChangePlan = () => {
        navigate("/pricing");
    };

    // Validation (simple example)
    const validate = () => {
        const errs = {};
        if (!form.name) errs.name = "Name required";
        if (!form.email) errs.email = "Email required";
        if (!form.phone) errs.phone = "Phone required";
        if (!form.plan) errs.plan = "Please select a plan";
        if (!form.companyInfo.name) errs.companyInfoName = "Company Name required";
        // ...add more as needed
        return errs;
    };

    // Helper to collect all attachments based on plan type
    function collectAttachments(form, planType) {
        const files = [];
        // General picture
        if (form.picture) files.push(form.picture);
        // Testimonials pictures
        if (form.testimonials && form.testimonials.length > 0) {
            form.testimonials.forEach(t => {
                if (t.picture) files.push(t.picture);
            });
        }
        // Company logo
        if (form.companyInfo && form.companyInfo.logo) files.push(form.companyInfo.logo);
        // Website main picture
        if ((planType === "website" || planType === "landing") && form.websiteMainPicture) files.push(form.websiteMainPicture);
        // Landing main
        if (planType === "landing" && form.landingMain) files.push(form.landingMain);
        // Listing pictures
        if (planType === "landing" && form.listing && form.listing.pictures && form.listing.pictures.length > 0) {
            form.listing.pictures.forEach(f => { if (f) files.push(f); });
        }
        return files;
    }

    // Reset form helper
    const reset = () => {
        setForm({
            ...form,
            name: "",
            email: "",
            phone: "",
            picture: null,
            plan: "",
            bio: "",
            quote: "",
            testimonials: [ { ...initialTestimonial } ],
            companyInfo: {
                name: "",
                phone: "",
                email: "",
                logo: null,
                address: ""
            },
            websiteMainPicture: null,
            websiteQuote: "",
            domain: "",
            postForYou: false,
            socialCredentials: {
                facebook: { username: "", password: "" },
                twitter: { username: "", password: "" },
                instagram: { username: "", password: "" },
                youtube: { username: "", password: "" },
                tiktok: { username: "", password: "" }
            },
            socialLinks: {
                twitter: "",
                facebook: "",
                instagram: "",
                youtube: "",
                linkedin: "",
                tiktok: ""
            },
            title: "",
            landingMain: null,
            listing: {
                address: "",
                postal: "",
                size: "",
                yearBuilt: "",
                lotSize: "",
                bedrooms: "",
                bathrooms: "",
                price: "",
                pictures: [],
                openHouses: [ { ...initialOpenHouse } ]
            },
            preferredTemplate: "",
        });
        setSelectedPlan("");
    };

    // Updated submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setIsSubmitting(true);
        try {
            const planType = getPlanType(form.plan);
            const content = buildEmailContent(form, planType);
            let attachments = collectAttachments(form, planType);

            // Calculate total size in bytes
            let totalSize = attachments.reduce((sum, file) => sum + (file.size || 0), 0);

            // 10MB = 10 * 1024 * 1024 bytes
            if (totalSize > 10 * 1024 * 1024) {
                // compress images
                attachments = await Promise.all(attachments.map(async file => {
                    if (file.type && file.type.startsWith('image/')) {
                        return await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920 });
                    }
                    return file;
                }));
                totalSize = attachments.reduce((sum, file) => sum + (file.size || 0), 0);
                if (totalSize > 10 * 1024 * 1024) {
                setError('Total attachment size exceeds 10MB. Please remove some files or use smaller images.');
                return;
                }
            }

            await sendEmail({
                recipientEmail: [process.env.REACT_APP_RECIPIENT_EMAIL, process.env.REACT_APP_RECIPIENT_EMAIL2, process.env.REACT_APP_RECIPIENT_EMAIL3, process.env.REACT_APP_RECIPIENT_EMAIL4],
                senderEmail: process.env.REACT_APP_SENDER_EMAIL,
                subject: `APTO Websites Signup Request (${form.plan})`,
                content,
                senderName: form.name,
                replyTo: form.email,
                attachments
            });
            setSuccess('Your signup request has been sent! We will contact you soon.');
            reset();
        } catch (error) {
            setError('There was an error sending your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="signup-outer-wrapper">
            <div className="signup-container">
                <h1>Sign Up</h1>
                <div className="selected-plan-section">
                    <label>Selected Plan:</label>
                    <div className="selected-plan-details">
                        <span>{selectedPlan || "No plan selected"}</span>
                        <button type="button" onClick={handleChangePlan} className="change-plan-btn">
                            Change Plan
                        </button>
                    </div>
                </div>
                <form className="signup-form" onSubmit={handleSubmit}>
                    {/* General Info */}
                    <section>
                        <h2>General Information</h2>
                        <div className="form-grid">
                            <div className="form-row">
                                <label>
                                    Name
                                    <span className={form.name ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.name && !form.name ? " required-error" : ""}`}
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Your full name"
                                />
                                {errors.name && !form.name && <span className="error">{errors.name}</span>}
                            </div>
                            <div className="form-row">
                                <label>
                                    Email
                                    <span className={form.email ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.email && !form.email ? " required-error" : ""}`}
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="you@email.com"
                                />
                                {errors.email && !form.email && <span className="error">{errors.email}</span>}
                            </div>
                            <div className="form-row">
                                <label>
                                    Phone
                                    <span className={form.phone ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.phone && !form.phone ? " required-error" : ""}`}
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="(555) 555-5555"
                                />
                                {errors.phone && !form.phone && <span className="error">{errors.phone}</span>}
                            </div>
                            <div className="form-row">
                                <label>Picture/Headshot</label>
                                <input className="styled-input" name="picture" type="file" accept="image/*" onChange={handleChange} />
                            </div>
                        </div>
                    </section>

                    {/* Bio/About Me */}
                    <section>
                        <h2>Bio / About Me</h2>
                        <div className="form-row">
                            <label>Bio/About Me</label>
                            <textarea className="styled-input" name="bio" value={form.bio} onChange={handleChange} placeholder="Tell us about yourself" />
                        </div>
                        <div className="form-row">
                            <label>Quote</label>
                            <input className="styled-input" name="quote" value={form.quote} onChange={handleChange} placeholder="Favorite quote" />
                        </div>
                    </section>

                    {/* Testimonials */}
                    <section>
                        <h2>Testimonials <button type="button" onClick={addTestimonial}>Add</button></h2>
                        {form.testimonials.map((t, idx) => (
                            <div className="testimonial-row" key={idx}>
                                <div className="form-grid">
                                    <div className="form-row">
                                        <label>Name</label>
                                        <input className="styled-input" value={t.name} onChange={e => handleTestimonialChange(idx, "name", e.target.value)} placeholder="Testimonial name" />
                                    </div>
                                    <div className="form-row">
                                        <label>Picture</label>
                                        <input className="styled-input" type="file" accept="image/*" onChange={e => handleTestimonialChange(idx, "picture", null, e.target.files)} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <label>Message</label>
                                    <textarea className="styled-input" value={t.message} onChange={e => handleTestimonialChange(idx, "message", e.target.value)} placeholder="Testimonial message" />
                                </div>
                                {/* Always show remove button */}
                                <button type="button" onClick={() => removeTestimonial(idx)} className="remove-btn">Remove</button>
                            </div>
                        ))}
                    </section>

                    {/* Company Info */}
                    <section>
                        <h2>Company Information</h2>
                        <div className="form-grid">
                            <div className="form-row">
                                <label>
                                    Name
                                    <span className={form.companyInfo.name ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.companyInfoName && !form.companyInfo.name ? " required-error" : ""}`}
                                    name="companyInfo.name"
                                    value={form.companyInfo.name}
                                    onChange={handleChange}
                                    placeholder="Company name"
                                />
                                {errors.companyInfoName && !form.companyInfo.name && <span className="error">{errors.companyInfoName}</span>}
                            </div>
                            <div className="form-row">
                                <label>Phone</label>
                                <input className="styled-input" name="companyInfo.phone" value={form.companyInfo.phone} onChange={handleChange} placeholder="(555) 555-5555" />
                            </div>
                            <div className="form-row">
                                <label>Email</label>
                                <input className="styled-input" name="companyInfo.email" value={form.companyInfo.email} onChange={handleChange} placeholder="company@email.com" />
                            </div>
                            <div className="form-row">
                                <label>Logo</label>
                                <input className="styled-input" name="companyInfo.logo" type="file" accept="image/*" onChange={handleChange} />
                            </div>
                            <div className="form-row" style={{gridColumn: "1 / -1"}}>
                                <label>Address</label>
                                <input className="styled-input" name="companyInfo.address" value={form.companyInfo.address} onChange={handleChange} placeholder="Company address" />
                            </div>
                        </div>
                    </section>

                    {/* Social Media Information FIRST if All-in-One Growth Bundle */}
                    {form.plan === "All-in-One Growth Bundle" && (() => {
                        const credPlatforms = SOCIAL_CREDENTIAL_PLATFORMS[form.plan] || ["facebook", "twitter", "instagram", "linkedin", "youtube", "tiktok"];
                        const linkPlatforms = SOCIAL_LINK_PLATFORMS[form.plan] || ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"];
                        const platformDisplay = {
                            twitter: "Twitter/X",
                            facebook: "Facebook",
                            instagram: "Instagram",
                            youtube: "YouTube",
                            linkedin: "LinkedIn",
                            tiktok: "TikTok"
                        };
                        const safeSocialCredentials = ensureSocialCredentials(form, credPlatforms);

                        return (
                            <section>
                                <h2>Social Media Information</h2>
                                {/* Show "Post for you?" and credentials for All-in-One Growth Bundle */}
                                <div className="form-row">
                                    <label>
                                        <input type="checkbox" name="postForYou" className="post-question" checked={form.postForYou} onChange={handleChange} />
                                        Post for you?
                                    </label>
                                </div>
                                {form.postForYou && (
                                    <div className="social-credentials">
                                        <h3>Account Credentials</h3>
                                        <div style={{ fontSize: "0.95em", color: "#666", marginBottom: "10px" }}>
                                            Each account is optional. Only provide credentials for the platforms you want us to post on. If you're missing an account, you can leave it blank.
                                        </div>
                                        <div className="form-grid signup-social-grid">
                                            {credPlatforms.map(platform => (
                                                <div className="form-row" key={platform}>
                                                    <label>{platformDisplay[platform]} Username</label>
                                                    <input
                                                        className="styled-input"
                                                        name={`socialCredentials.${platform}.username`}
                                                        value={safeSocialCredentials[platform]?.username || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>{platformDisplay[platform]} Password</label>
                                                    <input
                                                        className="styled-input"
                                                        name={`socialCredentials.${platform}.password`}
                                                        type="password"
                                                        value={safeSocialCredentials[platform]?.password || ""}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {/* Always show links */}
                                <div className="form-grid">
                                    {linkPlatforms.map(platform => (
                                        <div className="form-row" key={platform}>
                                            <label>{platformDisplay[platform]} Link</label>
                                            <input className="styled-input" name={`socialLinks.${platform}`} value={form.socialLinks[platform]} onChange={handleChange} placeholder={platformDisplay[platform]} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })()}

                    {/* Website Information (All-in-One Growth Bundle and website/landing) */}
                    {(form.plan === "All-in-One Growth Bundle" || planType === "website" || planType === "landing") && (
                        <section>
                            <h2>Website Information</h2>
                            <div className="form-grid">
                                <div className="form-row">
                                    <label>Main Picture</label>
                                    <input className="styled-input" name="websiteMainPicture" type="file" accept="image/*" onChange={handleChange} />
                                </div>
                                <div className="form-row">
                                    <label>Quote</label>
                                    <input className="styled-input" name="websiteQuote" value={form.websiteQuote} onChange={handleChange} placeholder="Website quote" />
                                </div>
                                <div className="form-row" style={{gridColumn: "1 / -1"}}>
                                    <label>Domain Name</label>
                                    <input className="styled-input" name="domain" value={form.domain} onChange={handleChange} placeholder="yourdomain.com" />
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Social Media Links for website/landing plans (excluding All-in-One Growth Bundle, which is handled above) */}
                    {(form.plan !== "All-in-One Growth Bundle" && (planType === "website" || planType === "landing")) && (() => {
                        // Use the default platforms for website/landing if not in SOCIAL_LINK_PLATFORMS
                        const linkPlatforms = SOCIAL_LINK_PLATFORMS[form.plan] || ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"];
                        const platformDisplay = {
                            twitter: "Twitter/X",
                            facebook: "Facebook",
                            instagram: "Instagram",
                            youtube: "YouTube",
                            linkedin: "LinkedIn",
                            tiktok: "TikTok"
                        };
                        return (
                            <section>
                                <h2>Social Media Links</h2>
                                <div className="form-grid">
                                    {linkPlatforms.map(platform => (
                                        <div className="form-row" key={platform}>
                                            <label>{platformDisplay[platform]} Link</label>
                                            <input className="styled-input" name={`socialLinks.${platform}`} value={form.socialLinks[platform]} onChange={handleChange} placeholder={platformDisplay[platform]} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })()}

                    {/* Social Media Information (for other social plans) */}
                    {(planType === "social" && form.plan !== "All-in-One Growth Bundle") && (() => {
                        const credPlatforms = SOCIAL_CREDENTIAL_PLATFORMS[form.plan] || ["facebook", "twitter", "instagram", "linkedin", "youtube", "tiktok"];
                        const linkPlatforms = SOCIAL_LINK_PLATFORMS[form.plan] || ["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"];
                        const platformDisplay = {
                            twitter: "Twitter/X",
                            facebook: "Facebook",
                            instagram: "Instagram",
                            youtube: "YouTube",
                            linkedin: "LinkedIn",
                            tiktok: "TikTok"
                        };
                        const safeSocialCredentials = ensureSocialCredentials(form, credPlatforms);

                        return (
                            <section>
                                <h2>Social Media Information</h2>
                                <div className="form-row">
                                    <label>
                                        <input type="checkbox" name="postForYou" className="post-question" checked={form.postForYou} onChange={handleChange} />
                                        Post for you?
                                    </label>
                                </div>
                                {form.postForYou && (
                                    <div className="social-credentials">
                                        <h3>Account Credentials</h3>
                                        <div style={{ fontSize: "0.95em", color: "#666", marginBottom: "10px" }}>
                                            Each account is optional. Only provide credentials for the platforms you want us to post on. If you're missing an account, you can leave it blank.
                                        </div>
                                        <div className="form-grid signup-social-grid">
                                            {credPlatforms.map(platform => (
                                                <div className="form-row" key={platform}>
                                                    <label>{platformDisplay[platform]} Username</label>
                                                    <input
                                                        className="styled-input"
                                                        name={`socialCredentials.${platform}.username`}
                                                        value={safeSocialCredentials[platform]?.username || ""}
                                                        onChange={handleChange}
                                                    />
                                                    <label>{platformDisplay[platform]} Password</label>
                                                    <input
                                                        className="styled-input"
                                                        name={`socialCredentials.${platform}.password`}
                                                        type="password"
                                                        value={safeSocialCredentials[platform]?.password || ""}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="form-grid">
                                    {linkPlatforms.map(platform => (
                                        <div className="form-row" key={platform}>
                                            <label>{platformDisplay[platform]} Link</label>
                                            <input className="styled-input" name={`socialLinks.${platform}`} value={form.socialLinks[platform]} onChange={handleChange} placeholder={platformDisplay[platform]} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })()}

                    {/* Landing Page Information */}
                    {(planType === "landing") && (
                        <section>
                            <h2>Landing Page Information</h2>
                            <div className="form-grid">
                                <div className="form-row">
                                    <label>Title (Realtor or Broker)</label>
                                    <input className="styled-input" name="title" value={form.title} onChange={handleChange} placeholder="Realtor/Broker" />
                                </div>
                                <div className="form-row">
                                    <label>Main Picture/Video</label>
                                    <input className="styled-input" name="landingMain" type="file" accept="image/*,video/*" onChange={handleChange} />
                                </div>
                                <div className="form-row">
                                    <label>Listing Address</label>
                                    <input className="styled-input" name="listing.address" value={form.listing.address} onChange={handleChange} placeholder="123 Main St" />
                                </div>
                                <div className="form-row">
                                    <label>Postal Code</label>
                                    <input className="styled-input" name="listing.postal" value={form.listing.postal} onChange={handleChange} placeholder="A1A 1A1" />
                                </div>
                                <div className="form-row">
                                    <label>Size (sq ft, Optional)</label>
                                    <input className="styled-input" name="listing.size" value={form.listing.size} onChange={handleChange} placeholder="e.g. 2000" />
                                </div>
                                <div className="form-row">
                                    <label>Year Built</label>
                                    <input className="styled-input" name="listing.yearBuilt" value={form.listing.yearBuilt} onChange={handleChange} placeholder="e.g. 2010" />
                                </div>
                                <div className="form-row">
                                    <label>Lot Size (sq ft, Optional)</label>
                                    <input className="styled-input" name="listing.lotSize" value={form.listing.lotSize} onChange={handleChange} placeholder="e.g. 5000" />
                                </div>
                                <div className="form-row">
                                    <label>Bedrooms</label>
                                    <input className="styled-input" name="listing.bedrooms" value={form.listing.bedrooms} onChange={handleChange} placeholder="e.g. 3" />
                                </div>
                                <div className="form-row">
                                    <label>Bathrooms</label>
                                    <input className="styled-input" name="listing.bathrooms" value={form.listing.bathrooms} onChange={handleChange} placeholder="e.g. 2" />
                                </div>
                                <div className="form-row">
                                    <label>Price</label>
                                    <input className="styled-input" name="listing.price" value={form.listing.price} onChange={handleChange} placeholder="$500,000" />
                                </div>
                                <div className="form-row" style={{gridColumn: "1 / -1"}}>
                                    <label>Additional Pictures/Videos</label>
                                    <input className="styled-input" name="listing.pictures" type="file" accept="image/*,video/*" multiple onChange={handleChange} />
                                </div>
                            </div>
                            <div className="open-houses-section">
                                <h3>Open House(s) <button type="button" onClick={addOpenHouse}>Add</button></h3>
                                <div className="form-grid">
                                    {form.listing.openHouses.map((o, idx) => (
                                        <div className="open-house-row" key={idx}>
                                            <div className="form-row">
                                                <label>Start Date</label>
                                                <input className="styled-input" type="datetime-local" value={o.start} onChange={e => handleOpenHouseChange(idx, "start", e.target.value)} />
                                            </div>
                                            <div className="form-row">
                                                <label>End Date</label>
                                                <input className="styled-input" type="datetime-local" value={o.end} onChange={e => handleOpenHouseChange(idx, "end", e.target.value)} />
                                            </div>
                                            {/* Always show remove button, even for the first */}
                                            <button type="button" onClick={() => removeOpenHouse(idx)} className="remove-btn">Remove</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Website Template Selection Section */}
                    {(planType === "website" || planType === "landing" || form.plan === "All-in-One Growth Bundle" || form.plan === "Hosting Bundle") && (
                        <section>
                            <h2>Choose Your Preferred Website Template</h2>
                            <div style={{display: "flex", flexWrap: "wrap", gap: "18px", justifyContent: "center"}}>
                                {WEBSITE_TEMPLATES.map(template => (
                                    <label
                                        key={template.id}
                                        style={{
                                            border: form.preferredTemplate === template.id ? "2px solid #1a4d8f" : "1.5px solid #e3eafc",
                                            borderRadius: "12px",
                                            boxShadow: form.preferredTemplate === template.id ? "0 4px 16px rgba(13,71,161,0.10)" : "0 2px 8px rgba(13,71,161,0.05)",
                                            padding: "12px",
                                            cursor: "pointer",
                                            background: "#fff",
                                            maxWidth: 220,
                                            minWidth: 180,
                                            flex: "1 1 180px",
                                            textAlign: "center",
                                            transition: "box-shadow 0.18s, border 0.18s"
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="preferredTemplate"
                                            value={template.id}
                                            checked={form.preferredTemplate === template.id}
                                            onChange={e => setForm(f => ({ ...f, preferredTemplate: e.target.value }))}
                                            style={{marginBottom: 8}}
                                        />
                                        <div style={{marginBottom: 8}}>
                                            <img
                                                src={template.img}
                                                alt={template.name}
                                                style={{
                                                    width: "100%",
                                                    maxWidth: 160,
                                                    borderRadius: 8,
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                                                    marginBottom: 6
                                                }}
                                            />
                                        </div>
                                        <div style={{fontWeight: 700, color: "#1a4d8f", fontSize: "1.05em"}}>{template.name}</div>
                                        {/* <div style={{fontSize: "0.93em", color: "#4a5a6a", marginTop: 2}}>{template.description}</div> */}
                                    </label>
                                ))}
                            </div>
                            {!form.preferredTemplate && (
                                <div style={{color: "#d32f2f", marginTop: 8, textAlign: "center", fontSize: "0.98em"}}>
                                    Please select a template.
                                </div>
                            )}
                        </section>
                    )}

                    {/* Show error or success message above the submit button */}
                    {error && <div className="error-message" role="alert">{error}</div>}
                    {success && (
                        <div className="success-message" role="status">
                            Your signup request has been sent! We will contact you soon.
                        </div>
                    )}
                    <button type="submit" className="submit-btn" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Signup;
